import { useCallback, useEffect, useState } from "react";
import type { FormEvent } from "react";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Alert } from "@/components/ui/Alert";
import { Spinner } from "@/components/ui/Spinner";
import { Table } from "@/components/ui/Table";
import { Dialog } from "@/components/ui/Dialog";
import { hasFormChanges } from "@/components/ui/dialogCloseGuard";
import { TextInput, TextareaInput } from "@/components/ui/Field";
import {
  createAnfrageSnippet,
  deleteAnfrageSnippet,
  listAnfrageSnippets,
  updateAnfrageSnippet,
  type AnfrageSnippet,
} from "../../lib/settingsApi";

export function AnfrageSnippetsSection() {
  const [items, setItems] = useState<AnfrageSnippet[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [form, setForm] = useState({ name: "", subject: "", body: "", sort_order: "0" });
  const [formBaseline, setFormBaseline] = useState(form);
  const [saving, setSaving] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await listAnfrageSnippets());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Snippets konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  function openEdit(row?: AnfrageSnippet) {
    setEditId(row?.id ?? null);
    const initial = {
      name: row?.name || "",
      subject: row?.subject || "",
      body: row?.body || "",
      sort_order: row?.sort_order != null ? String(row.sort_order) : "0",
    };
    setForm(initial);
    setFormBaseline(initial);
    setDialogOpen(true);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true);
    const payload = {
      name: form.name.trim(),
      subject: form.subject,
      body: form.body,
      sort_order: parseInt(form.sort_order, 10) || 0,
    };
    try {
      if (editId) await updateAnfrageSnippet(editId, payload);
      else await createAnfrageSnippet(payload);
      setDialogOpen(false);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card
      title="Technische Anfragen – Snippets"
      description="Vorlagen für Anfrage-Mails."
      actions={<Button type="button" onClick={() => openEdit()}>Neu</Button>}
    >
      {loading && <Spinner />}
      {error && <Alert variant="error" title="Fehler">{error}</Alert>}
      {!loading && (
        <Table<AnfrageSnippet>
          rowKey={(r) => r.id}
          rows={items}
          stickyEndColumnCount={1}
          columns={[
            {
              key: "sort",
              header: "Sort.",
              sortable: true,
              sortValue: (r) => r.sort_order,
              render: (r) => r.sort_order,
            },
            {
              key: "name",
              header: "Name",
              sortable: true,
              sortValue: (r) => r.name,
              render: (r) => r.name,
            },
            {
              key: "subject",
              header: "Betreff",
              sortable: true,
              sortValue: (r) => r.subject || "",
              render: (r) => {
                const s = r.subject || "";
                return s.length > 72 ? `${s.slice(0, 69)}…` : s || "—";
              },
            },
            {
              key: "actions",
              header: "",
              render: (r) => (
                <div className="flex justify-end gap-1">
                  <Button size="sm" variant="secondary" onClick={() => openEdit(r)}>Bearbeiten</Button>
                  <Button
                    size="sm"
                    variant="danger"
                    onClick={async () => {
                      if (!window.confirm("Snippet löschen?")) return;
                      await deleteAnfrageSnippet(r.id);
                      await reload();
                    }}
                  >
                    Löschen
                  </Button>
                </div>
              ),
            },
          ]}
        />
      )}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        dirty={hasFormChanges(form, formBaseline)}
        title={editId ? "Snippet bearbeiten" : "Neues Snippet"}
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={() => setDialogOpen(false)}>Abbrechen</Button>
            <Button onClick={handleSubmit} loading={saving}>Speichern</Button>
          </>
        }
      >
        <form className="space-y-3" onSubmit={handleSubmit}>
          <TextInput label="Name" required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
          <TextInput label="Betreff" value={form.subject} onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))} />
          <TextareaInput label="Text" rows={8} value={form.body} onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))} />
          <TextInput label="Sortierung" type="number" value={form.sort_order} onChange={(e) => setForm((f) => ({ ...f, sort_order: e.target.value }))} />
        </form>
      </Dialog>
    </Card>
  );
}
